import * as React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { ContainerTypeTable } from '@/components/ContainerTypeTable';
import { cn } from '@/lib/utils';
import {
  ChevronDown,
  ChevronRight,
  UserCircle,
  ArrowRight,
  RotateCcw,
  List,
} from 'lucide-react';
import { EventAccount } from '@/graphql/generatedTypes';
import { ItemTypeDisplay } from '@/components/ItemType/ItemTypeDisplay';
import { useIsMobile } from '@/hooks/use-mobile';
import i18n from '@/utils/i18n';

interface StockEntry {
  event_account_id: string;
  container_type_id: number;
  ordered: number; // boxes
  delivered: number; // boxes
  in_stock: number; // boxes
  handed_out: number; // boxes
}

interface ContainerTypeInfo {
  id: number;
  name: string;
  image_url?: string | null;
  packing_unit: number;
  color_hex_code?: string | null;
}

// Empty box entry (not linked to container type, but has image_url)
interface EmptyBoxReturnEntry {
  type: 'empty_box';
  event_account_id: string;
  count: number; // number of empty boxes
  image_url?: string | null;
  name?: string; // optional name/identifier for empty box type
  color_hex_code?: string | null; // hex color code for the empty box
}

type ReturnEntry = EmptyBoxReturnEntry;

export type EventAccountListItem = Pick<EventAccount, 'id' | 'name'>;

interface EventAccountStockSectionProps {
  eventAccount: EventAccountListItem;
  stockEntries: StockEntry[];
  containerTypes: ContainerTypeInfo[];
  returnedEntries: ReturnEntry[];
  isLoading?: boolean;
}

interface StockRow {
  type: string;
  typeId: number;
  imageUrl?: string | null;
  hexCode?: string | null;
  packingUnit: number;
  ordered: number; // containers
  delivered: number; // containers
  inStock: number; // containers
  handedOut: number; // containers
  returned: number; // containers (or boxes for empty boxes)
  isEmptyBox: boolean; // true if this is an empty box row
}

// Low stock threshold: less than this percentage of ordered boxes is considered low stock
const LOW_STOCK_THRESHOLD = 0.25; // 25%

export function EventAccountStockSection({
  eventAccount,
  stockEntries,
  containerTypes,
  returnedEntries,
  isLoading,
}: EventAccountStockSectionProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const isMobile = useIsMobile();

  // Calculate stock status badges based on individual rows
  // Low stock: any row with inStock <= 1 (containers) AND ordered > 0
  // No stock: any row with inStock === 0 (containers) AND ordered > 0
  const stockStatus = React.useMemo(() => {
    if (!stockEntries || stockEntries.length === 0 || !containerTypes)
      return { isEmpty: false, isLow: false };

    // Group by container type to match table rows
    const containerTypeMap = new Map<
      number,
      {
        ordered: number; // boxes
        inStock: number; // boxes
      }
    >();

    stockEntries.forEach((entry) => {
      const existing = containerTypeMap.get(entry.container_type_id) || {
        ordered: 0,
        inStock: 0,
      };
      existing.ordered += entry.ordered;
      existing.inStock += entry.in_stock;
      containerTypeMap.set(entry.container_type_id, existing);
    });

    // Check each row (container type) for low/no stock
    let hasEmptyStock = false;
    let hasLowStock = false;

    Array.from(containerTypeMap.entries()).forEach(
      ([containerTypeId, item]) => {
        const containerType = containerTypes.find(
          (ct) => ct.id === containerTypeId,
        );
        const packingUnit = containerType?.packing_unit || 1;
        // Convert boxes to containers for comparison
        const inStockContainers = Math.round(item.inStock * packingUnit);
        const orderedContainers = Math.round(item.ordered * packingUnit);

        // No stock: 0 containers AND ordered > 0
        if (inStockContainers === 0 && orderedContainers > 0) {
          hasEmptyStock = true;
        }
        // Low stock: <= 1 container AND ordered > 0
        if (inStockContainers <= 1 && orderedContainers > 0 && !hasEmptyStock) {
          hasLowStock = true;
        }
      },
    );

    return { isEmpty: hasEmptyStock, isLow: hasLowStock };
  }, [stockEntries, containerTypes]);

  // Calculate navigation badge values (in boxes)
  const navBadges = React.useMemo(() => {
    const handedOut =
      stockEntries?.reduce((sum, entry) => sum + entry.handed_out, 0) || 0;

    // Only count empty boxes for returned (single containers are NOT aggregated)
    const returned =
      returnedEntries?.reduce(
        (sum, entry) => sum + (entry.type === 'empty_box' ? entry.count : 0),
        0,
      ) || 0;

    const sum = returned - handedOut;

    return { handedOut, returned, sum };
  }, [stockEntries, returnedEntries]);

  // Process stock data into table rows - map directly from stockEntries
  const tableRows = React.useMemo<StockRow[]>(() => {
    const rows: StockRow[] = [];

    // Process container types from stockEntries
    if (stockEntries && stockEntries.length > 0) {
      // Group by container type and aggregate
      const containerTypeMap = new Map<
        number,
        {
          typeId: number;
          ordered: number;
          delivered: number;
          inStock: number;
          handedOut: number;
        }
      >();

      stockEntries.forEach((entry) => {
        const existing = containerTypeMap.get(entry.container_type_id) || {
          typeId: entry.container_type_id,
          ordered: 0,
          delivered: 0,
          inStock: 0,
          handedOut: 0,
        };
        existing.ordered += entry.ordered;
        existing.delivered += entry.delivered;
        existing.inStock += entry.in_stock;
        existing.handedOut += entry.handed_out;
        containerTypeMap.set(entry.container_type_id, existing);
      });

      // Convert container type entries to rows
      Array.from(containerTypeMap.values()).forEach((item) => {
        const containerType = containerTypes.find(
          (ct) => ct.id === item.typeId,
        );
        const packingUnit = containerType?.packing_unit || 1;
        // Convert boxes to containers by multiplying with packing_unit
        rows.push({
          type: containerType?.name || `Type ${item.typeId}`,
          typeId: item.typeId,
          imageUrl: containerType?.image_url || null,
          hexCode: containerType?.color_hex_code || null,
          packingUnit,
          ordered: Math.round(item.ordered * packingUnit), // boxes to containers
          delivered: Math.round(item.delivered * packingUnit), // boxes to containers
          inStock: Math.round(item.inStock * packingUnit), // boxes to containers
          handedOut: Math.round(item.handedOut * packingUnit), // boxes to containers
          returned: 0, // No single containers - returned is only for empty boxes which are not shown in table
          isEmptyBox: false,
        });
      });
    }

    // Empty boxes are NOT shown in the table, but are counted in aggregated stats

    return rows;
  }, [stockEntries, containerTypes, returnedEntries]);

  const formatBoxes = (
    amount: number,
    packingUnit: number,
    isEmptyBox: boolean = false,
  ): React.ReactNode => {
    if (amount === 0) return '0';
    if (isEmptyBox) {
      // For empty boxes, amount is already in boxes
      return `${amount}`;
    }
    const boxes = Math.floor(amount / packingUnit);
    return (
      <>
        {amount}{' '}
        <span className="tw:text-neutral-500">
          ({boxes} {i18n.t('Event:boxes')})
        </span>
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-2">
        <Skeleton className="tw:h-16 tw:w-full tw:rounded-lg" />
      </div>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            className={cn(
              'tw:bg-white tw:border tw:border-neutral-200 tw:rounded-lg tw:p-4 tw:w-full tw:cursor-pointer tw:hover:bg-neutral-50 tw:transition-colors',
              isMobile
                ? 'tw:flex tw:flex-col tw:gap-3 tw:items-start'
                : 'tw:flex tw:items-center tw:justify-between',
            )}
          >
            {isMobile ? (
              <>
                {/* Mobile: Row 1 - Event Account Name */}
                <div className="tw:flex tw:gap-2 tw:items-center tw:w-full">
                  <UserCircle className="tw:size-5 tw:text-neutral-700" />
                  <h3 className="tw:text-xl tw:font-semibold tw:leading-[1.2] tw:tracking-[-0.4px] tw:text-neutral-900">
                    {eventAccount.name || eventAccount.id}
                  </h3>
                </div>

                {/* Mobile: Row 2 - Stock Status Badges */}
                {(stockStatus.isEmpty || stockStatus.isLow) && (
                  <div className="tw:flex tw:gap-2 tw:items-center">
                    {stockStatus.isEmpty && (
                      <Badge variant="destructive">
                        {i18n.t('Event:stockEmpty')}
                      </Badge>
                    )}
                    {stockStatus.isLow && !stockStatus.isEmpty && (
                      <Badge variant="warning">
                        {i18n.t('Event:lowStock')}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Mobile: Row 3 - Navigation Badges and Chevron */}
                <div className="tw:flex tw:items-center tw:justify-between tw:w-full">
                  <div className="tw:flex tw:gap-2 tw:items-center">
                    {/* Handed Out Badge - Normal background */}
                    <Badge
                      variant="outline"
                      className="tw:border-neutral-200 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:flex tw:items-center tw:gap-1"
                    >
                      <ArrowRight className="tw:size-3" />
                      {navBadges.handedOut}
                    </Badge>
                    {/* Returned Badge - Normal background */}
                    <Badge
                      variant="outline"
                      className="tw:border-neutral-200 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:flex tw:items-center tw:gap-1"
                    >
                      <RotateCcw className="tw:size-3" />
                      {navBadges.returned}
                    </Badge>
                    {/* Sum Badge - Conditional colors */}
                    <Badge
                      variant={
                        navBadges.sum > 0
                          ? 'subtle'
                          : navBadges.sum === 0
                            ? 'success'
                            : 'destructive'
                      }
                      className="tw:flex tw:items-center tw:gap-1"
                    >
                      <List className="tw:size-3" />
                      {navBadges.sum > 0 ? '+' : ''}
                      {navBadges.sum}
                    </Badge>
                  </div>
                  {isOpen ? (
                    <ChevronDown className="tw:size-5 tw:text-neutral-700" />
                  ) : (
                    <ChevronRight className="tw:size-5 tw:text-neutral-700" />
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Desktop: Left side - Event Account Name and Stock Status Badges */}
                <div className="tw:flex tw:gap-2 tw:items-center">
                  <UserCircle className="tw:size-5 tw:text-neutral-700" />
                  <h3 className="tw:text-xl tw:font-semibold tw:leading-[1.2] tw:tracking-[-0.4px] tw:text-neutral-900">
                    {eventAccount.name || eventAccount.id}
                  </h3>
                  {stockStatus.isEmpty && (
                    <Badge variant="destructive">
                      {i18n.t('Event:stockEmpty')}
                    </Badge>
                  )}
                  {stockStatus.isLow && !stockStatus.isEmpty && (
                    <Badge variant="warning">{i18n.t('Event:lowStock')}</Badge>
                  )}
                </div>

                {/* Desktop: Right side - Navigation Badges and Chevron */}
                <div className="tw:flex tw:gap-4 tw:items-center">
                  <div className="tw:flex tw:gap-2 tw:items-center">
                    {/* Handed Out Badge - Normal background */}
                    <Badge
                      variant="outline"
                      className="tw:border-neutral-200 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:flex tw:items-center tw:gap-1"
                    >
                      <ArrowRight className="tw:size-3" />
                      {navBadges.handedOut}
                    </Badge>
                    {/* Returned Badge - Normal background */}
                    <Badge
                      variant="outline"
                      className="tw:border-neutral-200 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:flex tw:items-center tw:gap-1"
                    >
                      <RotateCcw className="tw:size-3" />
                      {navBadges.returned}
                    </Badge>
                    {/* Sum Badge - Conditional colors */}
                    <Badge
                      variant={
                        navBadges.sum > 0
                          ? 'subtle'
                          : navBadges.sum === 0
                            ? 'success'
                            : 'destructive'
                      }
                      className="tw:flex tw:items-center tw:gap-1"
                    >
                      <List className="tw:size-3" />
                      {navBadges.sum > 0 ? '+' : ''}
                      {navBadges.sum}
                    </Badge>
                  </div>
                  {isOpen ? (
                    <ChevronDown className="tw:size-5 tw:text-neutral-700" />
                  ) : (
                    <ChevronRight className="tw:size-5 tw:text-neutral-700" />
                  )}
                </div>
              </>
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="tw:bg-white tw:border tw:border-neutral-200 tw:rounded-lg tw:p-2 tw:mt-2">
            {isLoading ? (
              <div className="tw:p-4 tw:text-center tw:text-neutral-500">
                {i18n.t('Common:loading')}
              </div>
            ) : (
              <ContainerTypeTable
                columns={[
                  { key: 'type', label: i18n.t('Common:type'), width: '88px' },
                  {
                    key: 'ordered',
                    label: i18n.t('Event:ordered'),
                    width: 'flex-1',
                    headerContent: (
                      <p className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]">
                        {i18n.t('Event:ordered')}
                      </p>
                    ),
                  },
                  {
                    key: 'delivered',
                    label: i18n.t('Event:delivered'),
                    width: 'flex-1',
                    headerContent: (
                      <p className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]">
                        {i18n.t('Event:delivered')}
                      </p>
                    ),
                  },
                  {
                    key: 'in_stock',
                    label: i18n.t('Event:inStock'),
                    width: 'flex-1',
                    headerContent: (
                      <p className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]">
                        {i18n.t('Event:inStock')}
                      </p>
                    ),
                  },
                  {
                    key: 'handed_out',
                    label: i18n.t('Event:handedOut'),
                    width: 'flex-1',
                    headerContent: (
                      <p className="tw:text-sm tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]">
                        {i18n.t('Event:handedOut')}
                      </p>
                    ),
                  },
                ]}
                rows={tableRows.map((row) => {
                  // Low stock when less than threshold of ordered boxes is in stock
                  const isLowStock =
                    row.inStock > 0 &&
                    row.inStock < row.ordered * LOW_STOCK_THRESHOLD;
                  // Only show empty warning if ordered > 0 (if nothing was ordered, empty stock is expected)
                  const isEmptyStock = row.inStock === 0 && row.ordered > 0;

                  return {
                    id: `${row.isEmptyBox ? 'empty-box' : 'container-type'}-${row.typeId}`,
                    rowHeight: '60px',
                    cardTitle: (
                      <div className="tw:flex tw:gap-2 tw:items-center tw:min-w-0 tw:w-full">
                        <ItemTypeDisplay
                          imageUrl={row.imageUrl}
                          hexCode={row.hexCode}
                          alt={row.type}
                        />
                        <div className="tw:flex tw:items-center tw:h-12 tw:min-w-0 tw:flex-1">
                          <span className="tw:text-xs tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.18px] tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap tw:block tw:w-full">
                            {row.type}
                          </span>
                        </div>
                      </div>
                    ),
                    cells: [
                      <div
                        key="type"
                        className="tw:flex tw:items-start tw:relative tw:shrink-0"
                      >
                        <ItemTypeDisplay
                          imageUrl={row.imageUrl}
                          hexCode={row.hexCode}
                          alt={row.type}
                        />
                      </div>,
                      <Badge
                        key="ordered"
                        variant="outline"
                        className="tw:bg-neutral-50 tw:border-neutral-200 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:min-h-[24px]"
                      >
                        {formatBoxes(
                          row.ordered,
                          row.packingUnit,
                          row.isEmptyBox,
                        )}
                      </Badge>,
                      <Badge
                        key="delivered"
                        variant="outline"
                        className="tw:bg-neutral-50 tw:border-neutral-200 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:min-h-[24px]"
                      >
                        {formatBoxes(
                          row.delivered,
                          row.packingUnit,
                          row.isEmptyBox,
                        )}
                      </Badge>,
                      <Badge
                        key="in_stock"
                        variant={
                          isEmptyStock
                            ? 'destructive'
                            : isLowStock
                              ? 'warning'
                              : 'outline'
                        }
                        className={cn(
                          'tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:min-h-[24px]',
                          !isEmptyStock &&
                            !isLowStock &&
                            'tw:bg-neutral-50 tw:border-neutral-200',
                        )}
                      >
                        {row.inStock === 0
                          ? '0'
                          : formatBoxes(
                              row.inStock,
                              row.packingUnit,
                              row.isEmptyBox,
                            )}
                      </Badge>,
                      <Badge
                        key="handed_out"
                        variant="outline"
                        className="tw:bg-neutral-50 tw:border-neutral-200 tw:px-2 tw:py-1 tw:text-xs tw:font-medium tw:min-h-[24px]"
                      >
                        {formatBoxes(
                          row.handedOut,
                          row.packingUnit,
                          row.isEmptyBox,
                        )}
                      </Badge>,
                    ],
                  };
                })}
                isLoading={isLoading}
                emptyMessage={i18n.t('Event:noStockDataAvailable')}
                headerHeight="54px"
              />
            )}
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
